import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const FromEligibilityToEngagement: React.FC = () => {
  const navigate = useNavigate()

  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  const [activeId, setActiveId] = useState<string>('exec-summary')
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: 'exec-summary' },
    { id: 'front-door-problem' },
    { id: 'eligibility-not-static' },
    { id: 'where-system-breaks' },
    { id: 'cost-late-verification' },
    { id: 'eligibility-workflow-driver' },
    { id: 'case-study' },
    { id: 'modern-front-door' },
    { id: 'framework' },
    { id: 'measuring-success' },
    { id: 'conclusion' },
    { id: 'about' },
    { id: 'references' },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: '-40% 0px -50% 0px', threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: 'smooth' })
  }

  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  const imageBase = '/Image/WhitePapers/From Eligibility to Engagement'
  const fig1 = `${imageBase}/From Eligibility to Engagement Image 2.png`
  const fig2 = `${imageBase}/From Eligibility to Engagement Image 3.png`
  const fig3 = `${imageBase}/From Eligibility to Engagement Image 4.png`
  const fig4 = `${imageBase}/From Eligibility to Engagement Image 5.png`

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | Healthcare Access &amp; Operations
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            From Eligibility to Engagement
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Fixing the Broken Front Door to Care
          </p>
          <p className="mt-3 max-w-3xl text-slate-700 text-sm md:text-base">
            A practitioner’s guide to embedding eligibility earlier, reducing access friction, and converting more referrals into completed visits
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Collaborative Healthcare Platform" />
            <MetaItem label="Published" value="April 2026" />
            <MetaItem label="Length" value="16 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: 'From Eligibility to Engagement', url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? 'mt-10' : ''}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === 'exec-summary' && '⭐ Executive Summary'}
                {s.id === 'front-door-problem' && '🚪 1. The Front Door Problem in Healthcare Access'}
                {s.id === 'eligibility-not-static' && '🧠 2. Eligibility Is Not Static — And That’s the Problem'}
                {s.id === 'where-system-breaks' && '💥 3. Where the System Breaks'}
                {s.id === 'cost-late-verification' && '💸 4. The Cost of Late Eligibility Verification'}
                {s.id === 'eligibility-workflow-driver' && '🧭 5. Eligibility as a Workflow Driver, Not a Checkpoint'}
                {s.id === 'case-study' && '🧪 6. Case Study: Multi-Service Access Network'}
                {s.id === 'modern-front-door' && '🏁 7. What Good Looks Like: The Modern Front Door'}
                {s.id === 'framework' && '🛠️ 8. A Practical Framework for Implementation'}
                {s.id === 'measuring-success' && '📏 9. Measuring Success'}
                {s.id === 'conclusion' && '✅ Conclusion'}
                {s.id === 'about' && 'ℹ️ About EceptionistCX'}
                {s.id === 'references' && '📚 References'}
              </h2>

              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === 'exec-summary' && (
                  <>
                    <p>
                      Across healthcare, behavioral health, and public sector systems, access to care is often framed as a
                      capacity problem. More providers are needed. More funding is required. More services must be built.
                    </p>
                    <br />
                    <p>
                      But in many environments, the capacity already exists. The problem is not supply — it is navigation.
                      And one of the most significant, under-addressed barriers to successful navigation is eligibility
                      fragmentation.
                    </p>
                    <br />
                    <p>
                      Patients are frequently uncertain about their coverage, routed to providers outside their network,
                      delayed by manual verification processes, or lost between referral and first visit. Each of these
                      failures happens at the same place: the front door.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      The largest drop-off in care occurs between eligibility determination and first engagement — and it
                      is largely preventable.
                    </blockquote>
                    <br />
                    <p>
                      This white paper is a practitioner’s guide to fixing that front door. It examines where and why
                      eligibility breaks down, what it costs in operational and human terms, and how organizations can
                      restructure their intake and referral workflows to embed eligibility earlier — turning it from a
                      downstream checkpoint into a driver of the entire care access pathway.
                    </p>
                    <br />
                    <p>Organizations that make this shift can expect to:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-3">
                      <li>
                        <strong>Improve referral-to-visit conversion rates</strong> by routing patients to providers they
                        are actually covered for
                      </li>
                      <li>
                        <strong>Reduce scheduling rework and cancellations</strong> by catching eligibility issues before
                        appointments are booked
                      </li>
                      <li>
                        <strong>Unlock existing capacity</strong> by eliminating the friction that keeps available
                        providers underutilized
                      </li>
                      <li>
                        <strong>Deliver measurable improvements in access</strong> that satisfy performance requirements
                        tied to public funding
                      </li>
                    </ul>
                  </>
                )}

                {s.id === 'front-door-problem' && (
                  <>
                    <p>
                      The “front door” to care has never been wider. Digital intake forms, school-based referral programs,
                      community-based entry points, and health system portals have all expanded the ways patients can enter
                      the system. Yet despite this expansion, access has not become easier for most people navigating it.
                    </p>
                    <br />
                    <p>
                      Instead, patients encounter multiple intake processes with conflicting information, delays before
                      scheduling, and unclear next steps. What should be a single, navigable entry point has become a
                      fragmented maze — and the patients who struggle most with that complexity are often those with the
                      greatest need.
                    </p>
                    <br />
                    <p className="font-semibold">Who Is Most Affected</p>
                    <p>The complexity of the front door disproportionately affects:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Medicaid populations</strong> — who face dynamic coverage, frequent churn, and network
                        restrictions that vary by plan and provider
                      </li>
                      <li>
                        <strong>Children and families</strong> — who may be navigating school-based, community, and
                        clinical systems simultaneously
                      </li>
                      <li>
                        <strong>Individuals with co-occurring needs</strong> — who interact with both health and social
                        service systems, each with distinct intake requirements
                      </li>
                    </ul>
                    <br />
                    <p>
                      The result is a system with many open doors — but low rates of successful entry. As Figure 1
                      illustrates, completion rates to first visit vary significantly by entry point, and no channel
                      achieves even a 65% conversion rate without deliberate operational support.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 1. Patient Journey Complexity: Entry Points vs. First Visit Completion
                      </h5>
                      <img src={fig1} alt="Patient Journey Complexity: Entry Points vs. First Visit Completion" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <blockquote className="mt-4 border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      More entry points do not equal better access. Without operational infrastructure behind each door,
                      patients enter — and then disappear.
                    </blockquote>
                  </>
                )}

                {s.id === 'eligibility-not-static' && (
                  <>
                    <p>
                      Eligibility is frequently treated as a simple yes-or-no question: is this patient covered? In
                      practice, it is one of the most complex and dynamic variables in the care access process.
                    </p>
                    <br />
                    <p className="font-semibold">The Three Dimensions of Eligibility Complexity</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>It is dynamic.</strong> Coverage status changes frequently. Medicaid churn — the movement of
                        enrollees in and out of coverage — affects a significant portion of the Medicaid population
                        annually, according to the Kaiser Family Foundation. A patient who was eligible at referral may
                        not be eligible at scheduling. A patient who was ineligible last month may qualify today.
                      </li>
                      <li>
                        <strong>It is program-specific.</strong> Eligibility for Medicaid is not the same as eligibility
                        for a specific managed care plan, a school-based mental health program, a CCBHC grant-funded
                        service, or a county-funded program. Each has its own criteria, documentation requirements, and
                        expiration timelines. A patient may be Medicaid-enrolled but not covered for the specific service
                        being referred.
                      </li>
                      <li>
                        <strong>It is network-dependent.</strong> A provider may accept a patient’s insurance plan in
                        general, but not for a specific service line, location, or patient age group. Network
                        participation changes. Providers close panels. Contracts lapse. Without real-time network
                        visibility embedded into the referral workflow, patients are routinely sent to providers who
                        cannot actually serve them.
                      </li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Eligibility is not just a check — it is a decision point that shapes the entire downstream workflow.
                      Treating it as a checkbox creates friction at every subsequent step.
                    </blockquote>
                    <br />
                    <p className="font-semibold">The Consequence: Referrals That Should Not Have Been Made</p>
                    <p>
                      When eligibility is not confirmed before a referral is routed or a scheduling conversation begins, the downstream consequences are
                      predictable:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Patients are referred to out-of-network providers, requiring redirection and re-intake</li>
                      <li>Appointments are booked for services the patient’s plan does not cover, leading to cancellation at point of service</li>
                      <li>Patients receive conflicting information from different staff members, eroding trust and reducing engagement</li>
                      <li>Administrative staff spend disproportionate time correcting referrals that were misrouted at the start</li>
                    </ul>
                  </>
                )}

                {s.id === 'where-system-breaks' && (
                  <>
                    <p>
                      Across organizations, the most common failure pattern follows a predictable sequence. Understanding it
                      is the first step to redesigning it.
                    </p>
                    <br />
                    <p className="font-semibold">The Typical Breakdown Sequence</p>
                    <ul className="list-disc pl-6 space-y-4 mt-2">
                      <li>
                        <strong>Step 1 — Referral is initiated.</strong> A provider, school counselor, or community
                        organization submits a referral. It enters a queue.
                      </li>
                      <li>
                        <strong>Step 2 — Intake is partially completed.</strong> Staff begin collecting information but do
                        not yet confirm eligibility. The intake proceeds on the assumption that coverage will be sorted
                        out later.
                      </li>
                      <li>
                        <strong>Step 3 — Scheduling begins.</strong> An appointment is booked — sometimes before
                        eligibility is confirmed, sometimes before the appropriate provider is identified.
                      </li>
                      <li>
                        <strong>Step 4 — Eligibility is checked late — or not at all.</strong> Coverage is verified close
                        to the appointment date, or at check-in. By this point, any issue requires rescheduling,
                        redirection, or cancellation.
                      </li>
                      <li>
                        <strong>Step 5 — Appointment is disrupted.</strong> The patient does not receive care. They may or
                        may not re-engage. Each disruption increases the probability of permanent drop-off.
                      </li>
                    </ul>
                    <br />
                    <p>
                      As Figure 2 illustrates, this breakdown sequence produces significant attrition at each stage of the care journey. A system that starts
                      with 100 referrals often delivers completed first visits to fewer than half — not because of provider shortage, but because of operational friction.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 2. Drop-Off Between Referral and First Visit
                      </h5>
                      <img src={fig2} alt="Drop-Off Between Referral and First Visit" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <br />
                    <p className="font-semibold">The Hidden Cost: Rework</p>
                    <p>
                      Each point of breakdown does not just represent a lost patient — it represents a cycle of rework. Staff re-contact patients. Referrals are
                      re-routed. Intake processes are restarted. Provider schedules are disrupted. The administrative burden accumulates invisibly, consuming the
                      capacity of teams that could otherwise be focused on bringing more patients into care.
                    </p>
                  </>
                )}

                {s.id === 'cost-late-verification' && (
                  <>
                    <p>
                      Late eligibility verification does not just inconvenience patients. It generates measurable operational,
                      financial, and systemic costs that compound over time.
                    </p>
                    <br />
                    <p className="font-semibold">Operational Impact</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Scheduling inefficiencies</strong> — Appointments booked before eligibility is confirmed
                        must frequently be rescheduled or redirected, fragmenting provider calendars and consuming
                        coordinator time
                      </li>
                      <li>
                        <strong>Repeated outreach cycles</strong> — Each re-contact attempt carries a declining probability
                        of patient response, particularly for populations with limited phone access, transportation
                        barriers, or high levels of healthcare-related distrust
                      </li>
                      <li>
                        <strong>Increased administrative workload</strong> — The CAQH Index estimates that eligibility
                        verification and related administrative processes cost the U.S. healthcare system tens of billions
                        annually. Late or inconsistent verification multiplies this burden by requiring rework at multiple
                        touchpoints
                      </li>
                    </ul>
                    <br />
                    <p className="font-semibold">Patient Impact</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Missed or delayed care</strong> — Every disruption between referral and first visit
                        represents a period during which the patient’s condition may worsen without intervention
                      </li>
                      <li>
                        <strong>Confusion and eroded trust</strong> — Patients who receive conflicting information about
                        their coverage or who are redirected multiple times are significantly less likely to re-engage
                      </li>
                      <li>
                        <strong>Reduced likelihood of sustained engagement</strong> — Research consistently shows that the
                        probability of long-term engagement drops sharply with each failed or delayed first-visit attempt
                      </li>
                    </ul>
                    <br />
                    <p className="font-semibold">System Impact</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Underutilized provider capacity</strong> — Providers hold appointment slots for patients
                        who will not arrive, while other eligible patients wait
                      </li>
                      <li>
                        <strong>Lower conversion rates against funding metrics</strong> — Programs funded under ARPA,
                        Medicaid, or state behavioral health grants are increasingly measured on utilization and access
                        outcomes. Low conversion rates jeopardize funding accountability
                      </li>
                      <li>
                        <strong>Inequitable outcomes across populations</strong> — Late eligibility verification
                        disproportionately affects patients with the fewest resources to navigate a disrupted care
                        pathway
                      </li>
                    </ul>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 3. Early vs. Late Eligibility Verification: Operational Outcomes
                      </h5>
                      <img src={fig3} alt="Early vs. Late Eligibility Verification: Operational Outcomes" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      The question is not whether eligibility verification matters. The question is when it happens — and
                      every step it is delayed, the cost compounds.
                    </blockquote>
                  </>
                )}

                {s.id === 'eligibility-workflow-driver' && (
                  <>
                    <p>
                      The most important conceptual shift organizations can make is this: eligibility is not a step in the workflow.
                      It is a driver of the workflow.
                    </p>
                    <br />
                    <p>
                      When eligibility is treated as a checkpoint — something to confirm before an appointment — it becomes a source of
                      friction and failure. When it is treated as a driver — something that shapes every decision from the moment a
                      referral arrives — it becomes a source of efficiency and precision.
                    </p>
                    <br />
                    <p className="font-semibold">What Changes When Eligibility Drives the Workflow</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>It informs where the referral goes.</strong> Instead of routing to the nearest or most familiar provider, the system routes
                        to the provider the patient is actually covered for, in the network their plan participates in, for the specific
                        service being requested.
                      </li>
                      <li>
                        <strong>It determines which providers are available.</strong> Scheduling surfaces only those options that match the patient’s confirmed
                        coverage — eliminating the possibility of booking an out-of-network appointment and the cascade of rework that follows.
                      </li>
                      <li>
                        <strong>It shapes how scheduling is executed.</strong> When eligibility is confirmed, scheduling is efficient. When eligibility is pending
                        or uncertain, the system flags the case for resolution before proceeding rather than moving forward and creating a problem
                        downstream.
                      </li>
                      <li>
                        <strong>It prevents incorrect referrals before they are made.</strong> Instead of catching errors at the point of service, the system catches
                        them at the point of intake — where correction is simple, fast, and has no impact on the patient’s care journey.
                      </li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Eligibility embedded early does not just improve access metrics. It changes the fundamental architecture of how care is coordinated.
                    </blockquote>
                  </>
                )}

                {s.id === 'case-study' && (
                  <>
                    <p>
                      <span className="font-semibold">Overview</span>
                      <br />
                      A regional network integrating healthcare providers, behavioral health services, and community-based programs faced
                      persistent access challenges despite stable funding and adequate provider capacity. The organization had invested in
                      electronic health records, a referral management system, and patient outreach resources — yet conversion rates from
                      referral to first visit remained well below target.
                    </p>
                    <br />
                    <p>
                      A workflow analysis revealed the root cause: eligibility verification was occurring after scheduling, creating a
                      consistent pattern of cancellations, redirections, and patient drop-off that no amount of outreach could fully
                      compensate for.
                    </p>
                    <br />
                    <p className="font-semibold">Initial State: Operational Environment</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li><strong>Multiple intake points</strong> across member agencies, each with distinct forms, processes, and staff workflows</li>
                      <li><strong>Manual eligibility verification</strong> initiated by coordinators after scheduling was underway, often via phone or portal lookup</li>
                      <li><strong>No shared visibility</strong> across organizations — each agency tracked its own cases without awareness of what was happening elsewhere in the network</li>
                      <li><strong>Scheduling initiated before eligibility confirmation</strong> as a matter of standard practice, creating a structural gap between booking and coverage confirmation</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Baseline Performance</p>
                    <p>The table below captures the network’s baseline conversion rates across the care access funnel:</p>
                    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Stage</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Before Volume</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Before Rate</th>
                            <th className="px-4 py-3 text-left font-semibold text-emerald-900 bg-emerald-50">After Volume</th>
                            <th className="px-4 py-3 text-left font-semibold text-emerald-900 bg-emerald-50">After Rate</th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-medium">Referrals Initiated</td>
                            <td className="px-4 py-3">100</td>
                            <td className="px-4 py-3">—</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">100</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">—</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Intake Completed</td>
                            <td className="px-4 py-3">74</td>
                            <td className="px-4 py-3">74%</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">89</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">89%</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Appointments Scheduled</td>
                            <td className="px-4 py-3">58</td>
                            <td className="px-4 py-3">58%</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">81</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">81%</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">First Visits Completed</td>
                            <td className="px-4 py-3">41</td>
                            <td className="px-4 py-3">41%</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">68</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">68%</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">30-Day Retention</td>
                            <td className="px-4 py-3">29</td>
                            <td className="px-4 py-3">29%</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">54</td>
                            <td className="px-4 py-3 bg-emerald-50 text-emerald-900">54%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Nearly 60% of patients who entered the system failed to complete a first visit — not because services were unavailable,
                      but because operational friction prevented them from getting there.
                    </blockquote>
                    <br />
                    <p className="font-semibold">Intervention: Eligibility-Centered Workflow Redesign</p>
                    <p>
                      The organization restructured its front-end workflows around a single design principle: eligibility must be confirmed before
                      any scheduling action is taken.
                    </p>
                    <br />
                    <p>The key operational changes were:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li><strong>Eligibility verification integrated into the intake process</strong> — Coverage was checked at the point of referral using automated connections to payer and program data sources</li>
                      <li><strong>Referral routing informed by coverage and network participation</strong> — Referrals were directed only to providers confirmed to accept the patient’s plan for the specific service requested</li>
                      <li><strong>Scheduling initiated only after eligibility confirmation</strong> — The workflow enforced this sequencing, preventing coordinators from booking appointments before coverage was verified</li>
                      <li><strong>Shared visibility across organizations</strong> — A common operational view allowed all network members to see case status, eligibility flags, and next steps in real time</li>
                    </ul>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 4. Case Study: Before &amp; After Eligibility-Centered Workflow Redesign
                      </h5>
                      <img src={fig4} alt="Before and After Eligibility-Centered Workflow Redesign" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <br />
                    <p className="font-semibold">Key Takeaway</p>
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Moving eligibility upstream in the workflow fundamentally improved access — not by adding capacity, but by reducing friction.
                    </blockquote>
                    <br />
                    <p>
                      The gains were not attributable to new providers, additional funding, or expanded hours. They were the direct result of redesigning
                      when eligibility information entered the workflow and how it shaped downstream decisions. The same referral volume produced dramatically
                      better outcomes simply because fewer patients were lost to preventable operational failures.
                    </p>
                  </>
                )}

                {s.id === 'modern-front-door' && (
                  <>
                    <p>
                      A modern access model does not look like a more sophisticated version of the current system. It looks fundamentally
                      different — because the sequence of decisions is different. Eligibility comes first, and everything else is organized around it.
                    </p>
                    <br />
                    <p className="font-semibold">Five Characteristics of a High-Performing Front Door</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Guided intake and referral pathways</strong> — Every referral follows a structured,
                        digital workflow with defined triage logic, ownership, and routing rules.
                      </li>
                      <li>
                        <strong>Real-time eligibility visibility</strong> — Coverage is confirmed at the moment of intake,
                        using live connections to payer and program data.
                      </li>
                      <li>
                        <strong>Eligibility-aligned scheduling</strong> — Scheduling surfaces only provider options
                        consistent with the patient’s confirmed coverage.
                      </li>
                      <li>
                        <strong>Clear next steps for patients and staff</strong> — Every patient knows what to expect next
                        and what is required of them.
                      </li>
                      <li>
                        <strong>Cross-organization transparency</strong> — In multi-agency networks, all relevant parties
                        share a common view of where a patient is in the journey and who owns the next step.
                      </li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      This is not a better front door. It is a different kind of front door — one that is built around confirmed
                      eligibility rather than assumed eligibility.
                    </blockquote>
                  </>
                )}

                {s.id === 'framework' && (
                  <>
                    <p>
                      Organizations can implement eligibility-centered workflows through a phased approach. The phases below are sequenced
                      to deliver early wins while building toward a fully integrated operating model. Organizations at different starting
                      points may enter the framework at different phases, but each phase is most effective when the prior ones are in place.
                    </p>
                    <br />
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">Phase 1 — Standardize Intake</td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Establish a single, consistent digital intake pathway across all entry points</li>
                                <li>Create standardized triage criteria to route patients based on need, coverage, and program eligibility</li>
                                <li>Assign clear ownership for intake steps and ensure every referral enters a tracked queue</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">Phase 2 — Embed Eligibility Early</td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Verify coverage at the point of referral — before scheduling begins</li>
                                <li>Connect to payer and program data sources to check network participation and benefit coverage</li>
                                <li>Flag eligibility issues in real time and route patients to appropriate funding sources automatically</li>
                                <li>Re-verify eligibility for longitudinal cases before each scheduled appointment</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">Phase 3 — Align Scheduling to Eligibility</td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Filter provider and appointment options based on confirmed coverage and network participation</li>
                                <li>Eliminate scheduling steps that precede eligibility confirmation</li>
                                <li>Automate appointment reminders with eligibility status embedded to reduce last-minute cancellations</li>
                                <li>Capture eligibility exceptions as structured data to drive continuous improvement</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">Phase 4 — Track Conversion and Optimize</td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Monitor referral-to-visit conversion rates at each stage of the workflow</li>
                                <li>Track eligibility exception rates, scheduling friction, and no-show patterns by program and population</li>
                                <li>Use performance data to refine routing logic, intake workflows, and provider matching</li>
                                <li>Tie operational metrics to funding accountability and reporting requirements</li>
                              </ul>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <p>
                      A common implementation pitfall is attempting Phase 4 — measurement and optimization — before Phases 1 through 3 are in place.
                      Tracking conversion rates without first standardizing intake and embedding eligibility produces data that reveals problems but
                      cannot address them, because the operational changes needed to move the metrics have not yet been made.
                    </p>
                  </>
                )}

                {s.id === 'measuring-success' && (
                  <>
                    <p>
                      Eligibility-centered workflows produce measurable outcomes that can be tracked, reported, and used to demonstrate program
                      value to funders, leadership, and accreditation bodies. The metrics below form a practical dashboard for organizations
                      implementing this model.
                    </p>
                    <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Metric</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">What It Measures</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Why It Matters</th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-medium">Referral-to-Visit Conversion Rate</td>
                            <td className="px-4 py-3">Percentage of initiated referrals that result in a completed first visit</td>
                            <td className="px-4 py-3">Primary access outcome measure</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Time to First Appointment</td>
                            <td className="px-4 py-3">Average days from referral initiation to first completed visit</td>
                            <td className="px-4 py-3">Reflects scheduling efficiency and eligibility lag</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Eligibility Exception Rate</td>
                            <td className="px-4 py-3">Percentage of referrals disrupted due to eligibility issues post-scheduling</td>
                            <td className="px-4 py-3">Direct measure of early vs. late verification impact</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">No-Show / Cancellation Rate</td>
                            <td className="px-4 py-3">Percentage of scheduled appointments that do not result in a visit</td>
                            <td className="px-4 py-3">Indicates patient engagement and scheduling appropriateness</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Intake Completion Rate</td>
                            <td className="px-4 py-3">Percentage of referrals that progress through a complete intake</td>
                            <td className="px-4 py-3">Reveals front-door friction and intake process quality</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">30-Day Retention Rate</td>
                            <td className="px-4 py-3">Percentage of patients returning for a second visit within 30 days</td>
                            <td className="px-4 py-3">Measures engagement beyond the first-visit threshold</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <p>
                      These metrics are most powerful when tracked longitudinally and segmented by population, program type, and entry point.
                      An organization may have strong overall conversion rates but poor performance for Medicaid populations specifically — a
                      pattern that would be invisible without segmentation, and that points to specific eligibility or network issues requiring
                      attention.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      What gets measured gets managed — but measurement alone does not create improvement. The operational
                      infrastructure to act on what the data reveals must be in place first.
                    </blockquote>
                  </>
                )}

                {s.id === 'conclusion' && (
                  <>
                    <p>
                      The challenge of access to care is not simply a challenge of supply. It is a challenge of navigation. And the most consequential
                      navigation failure in most systems happens at the very beginning — before a patient ever sees a provider.
                    </p>
                    <br />
                    <p>
                      Eligibility sits at the center of that failure. When it is treated as a downstream check, it generates friction, rework,
                      cancellations, and patient drop-off at every step of the care access journey. When it is treated as a workflow driver —
                      confirmed early, embedded into routing and scheduling, and visible across organizations — the same resources produce dramatically
                      better outcomes.
                    </p>
                    <p>
                      The case study presented in this paper demonstrates that these improvements are not theoretical. They are achievable without adding
                      providers, expanding hours, or increasing funding. They require operational redesign — specifically, the decision to make eligibility
                      the organizing principle of the front door rather than an afterthought at the back.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Organizations that fix the front door will unlock the capacity they already have.
                    </blockquote>
                    <br />
                    <p>
                      The next phase of access improvement will not be won by organizations that spend the most. It will be won by organizations that execute
                      most effectively — and execution begins with eligibility.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}

                {s.id === 'about' && (
                  <>
                    <p>
                      EceptionistCX is a collaborative care coordination platform designed to support complex referral, scheduling, and eligibility workflows
                      across healthcare, public sector, and community-based environments.
                    </p>
                    <br />
                    <p>
                      The platform helps organizations improve access, reduce administrative burden, and create clearer pathways to care — particularly in systems
                      where coordination across multiple stakeholders and eligibility programs is essential. EceptionistCX has been delivering secure, cloud-based
                      workflow solutions for more than two decades, supporting healthcare organizations around the world.
                    </p>
                    <br />
                    <p>
                      EceptionistCX capabilities include real-time eligibility verification, guided referral pathways, coordinated scheduling workflows,
                      cross-organization visibility, and longitudinal case tracking. The platform is designed to complement existing systems of record — not replace
                      them — by providing the operational infrastructure that turns eligibility data into access outcomes.
                    </p>
                  </>
                )}

                {s.id === 'references' && (
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Kaiser Family Foundation (KFF). Medicaid Enrollment and Churn Data. kff.org.</li>
                    <li>CAQH Index Reports. Closing the Gap: The Industry’s Call to Action. caqh.org.</li>
                    <li>Centers for Medicare and Medicaid Services (CMS). Medicaid Managed Care Enrollment and Program Characteristics. medicaid.gov.</li>
                    <li>Health Resources and Services Administration (HRSA). Health Professional Shortage Area (HPSA) Data. hrsa.gov.</li>
                    <li>American Rescue Plan Act (2021). Certified Community Behavioral Health Clinic (CCBHC) Expansion Grant Program. Public Law 117-2.</li>
                    <li>JAMA Network Open. Behavioral health referral conversion rates, no-show patterns, and access research. jamanetwork.com.</li>
                    <li>National Association of State Mental Health Program Directors (NASMHPD). Behavioral health workforce and access data. nasmhpd.org.</li>
                  </ol>
                )}
              </div>
            </section>
          ))}
        </article>

        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                The biggest drop-off in access often happens between eligibility and first engagement. By moving eligibility upstream
                and making it drive routing and scheduling, organizations can reduce friction and convert more referrals into completed visits.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Eligibility fragmentation at the front door</li>
                <li>Operational costs of late verification</li>
                <li>Eligibility-aligned routing and scheduling</li>
                <li>Multi-agency visibility and accountability</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((sec) => (
                  <li key={sec.id}>
                    <button
                      onClick={() => scrollTo(sec.id)}
                      className={
                        'text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 ' +
                        (activeId === sec.id ? 'font-semibold text-slate-900' : 'text-slate-700')
                      }
                    >
                      {sec.id === 'exec-summary' && '⭐ Executive Summary'}
                      {sec.id === 'front-door-problem' && '🚪 1. The Front Door Problem'}
                      {sec.id === 'eligibility-not-static' && '🧠 2. Eligibility Is Not Static'}
                      {sec.id === 'where-system-breaks' && '💥 3. Where the System Breaks'}
                      {sec.id === 'cost-late-verification' && '💸 4. The Cost of Late Verification'}
                      {sec.id === 'eligibility-workflow-driver' && '🧭 5. Eligibility as a Workflow Driver'}
                      {sec.id === 'case-study' && '🧪 6. Case Study'}
                      {sec.id === 'modern-front-door' && '🏁 7. The Modern Front Door'}
                      {sec.id === 'framework' && '🛠️ 8. Practical Framework'}
                      {sec.id === 'measuring-success' && '📏 9. Measuring Success'}
                      {sec.id === 'conclusion' && '✅ Conclusion'}
                      {sec.id === 'about' && 'ℹ️ About EceptionistCX'}
                      {sec.id === 'references' && '📚 References'}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Collaborative Healthcare Platform. &quot;From Eligibility to Engagement: Fixing the Broken Front Door to Care.&quot; April 2026.
              </p>
              <button
                onClick={() => {
                  const text =
                    'EceptionistCX Collaborative Healthcare Platform. \"From Eligibility to Engagement: Fixing the Broken Front Door to Care.\" April 2026.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'From Funding to Access', description: 'Why public health investments fail without operational infrastructure.', route: '/whitepapers/from-funding-to-access' },
              { title: 'Bridging the Youth Mental Health Access Gap', description: 'Coordination and eligibility automation unlock timely care.', route: '/whitepapers/bridging-youth-mental-health-access-gap' },
              { title: 'The Future of Referral Management in Healthcare', description: 'From fax to AI-powered referral workflows.', route: '/whitepapers/referral-cx' },
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      <div className="h-8" />

      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="From Eligibility to Engagement: Fixing the Broken Front Door to Care"
        pdfPath="/PDF/WhitePapers/From_Eligibility_to_Engagement_WhitePaper.pdf"
      />
    </div>
  )
}

export default FromEligibilityToEngagement

